import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useEffect, useMemo, useState } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import { getScenarioForMission } from '../data/scenarios';

interface Section {
  title: string;
  body: string;
}

interface DiagramNode {
  label: string;
  color: string;
  textColor?: string;
}

interface DiagramRow {
  nodes: DiagramNode[];
  arrow?: boolean;
}

interface ConceptCard {
  id: number;
  sequenceItemId?: string;
  title: string;
  subtitle: string;
  color: string;
  icon: string;
  sections: Section[];
  diagram?: {
    title: string;
    rows: DiagramRow[];
  };
  isRead: boolean;
  isBookmarked: boolean;
}

type MarkupBlock =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'unordered-list'; items: string[] }
  | { type: 'ordered-list'; items: string[] }
  | { type: 'table'; rows: string[][] }
  | { type: 'blockquote'; text: string };

const EGOCRISIS_CARDS: ConceptCard[] = [
  {
    id: 1,
    sequenceItemId: 'legacy-1',
    title: 'RBAC dans Entra ID',
    subtitle: 'Role-Based Access Control — controle des acces par role',
    color: '#3B82F6',
    icon: 'shield-checkmark',
    isRead: false,
    isBookmarked: false,
    sections: [
      {
        title: 'Principe',
        body: "Le RBAC (Role-Based Access Control) permet d'attribuer des permissions a des utilisateurs via des roles, sans leur donner d'acces direct aux ressources. Entra ID utilise RBAC pour tous les services Azure.",
      },
      {
        title: 'Composants cles',
        body: "Role Definition : ensemble de permissions (actions, notActions, dataActions). Security Principal : utilisateur, groupe, service principal ou identite geree. Scope : abonnement, groupe de ressources, ou ressource specifique.",
      },
      {
        title: 'Bonnes pratiques',
        body: "Appliquer le principe du moindre privilege. Utiliser des groupes plutot que des assignations individuelles. Preferer les roles integres aux roles personnalises quand possible.",
      },
    ],
    diagram: {
      title: "Flux d'une Role Assignment",
      rows: [
        { nodes: [{ label: 'Utilisateur / Groupe', color: '#3B82F6', textColor: '#FFF' }] },
        { nodes: [{ label: '', color: 'transparent' }], arrow: true },
        { nodes: [{ label: 'Role Assignment', color: '#8B5CF6', textColor: '#FFF' }] },
        { nodes: [{ label: '', color: 'transparent' }], arrow: true },
        { nodes: [
          { label: 'Reader', color: '#10B981', textColor: '#FFF' },
          { label: 'Contributor', color: '#F59E0B', textColor: '#FFF' },
          { label: 'Owner', color: '#EF4444', textColor: '#FFF' },
        ]},
        { nodes: [{ label: '', color: 'transparent' }], arrow: true },
        { nodes: [{ label: 'Ressource Azure (scope)', color: '#6B7280', textColor: '#FFF' }] },
      ],
    },
  },
  {
    id: 2,
    sequenceItemId: 'legacy-2',
    title: 'Groupes Dynamiques',
    subtitle: 'Adhesion automatique basee sur des attributs',
    color: '#10B981',
    icon: 'people',
    isRead: false,
    isBookmarked: true,
    sections: [
      {
        title: "Qu'est-ce que c'est ?",
        body: "Un groupe dynamique dans Entra ID se peuple automatiquement selon des regles basees sur les attributs des utilisateurs (departement, titre, localisation...). Plus besoin de gestion manuelle.",
      },
      {
        title: 'Exemple de regle',
        body: '(user.department -eq "Finance") -and (user.accountEnabled -eq true)\nTous les utilisateurs du departement Finance avec un compte actif sont automatiquement membres.',
      },
      {
        title: 'Prerequis',
        body: "Requiert une licence Microsoft Entra ID P1 minimum. Les groupes dynamiques peuvent etre de type Securite ou Microsoft 365. La reevaluation des regles peut prendre jusqu'a 24h.",
      },
    ],
    diagram: {
      title: 'Groupes statiques vs dynamiques',
      rows: [
        { nodes: [
          { label: 'Statique : ajout manuel', color: '#EF4444', textColor: '#FFF' },
          { label: 'Dynamique : regle auto', color: '#10B981', textColor: '#FFF' },
        ]},
        { nodes: [{ label: '', color: 'transparent' }], arrow: true },
        { nodes: [
          { label: 'Admin ajoute manuellement', color: '#FEE2E2', textColor: '#7F1D1D' },
          { label: 'Attribut departement = Finance', color: '#D1FAE5', textColor: '#064E3B' },
        ]},
        { nodes: [{ label: '', color: 'transparent' }], arrow: true },
        { nodes: [
          { label: "Risque d'oubli / erreur", color: '#FCA5A5', textColor: '#7F1D1D' },
          { label: 'Toujours a jour automatiquement', color: '#6EE7B7', textColor: '#064E3B' },
        ]},
      ],
    },
  },
  {
    id: 3,
    sequenceItemId: 'legacy-3',
    title: 'Zero Trust Architecture',
    subtitle: 'Ne jamais faire confiance, toujours verifier',
    color: '#8B5CF6',
    icon: 'lock-closed',
    isRead: true,
    isBookmarked: false,
    sections: [
      {
        title: 'Principe fondamental',
        body: "Zero Trust part du principe qu'aucun utilisateur, appareil ou reseau ne doit etre considere comme fiable par defaut — meme s'il est deja dans le perimetre de l'entreprise. Chaque acces est verifie.",
      },
      {
        title: 'Les 3 piliers',
        body: "1. Verifier explicitement : authentifier et autoriser systematiquement avec tous les points de donnees disponibles.\n2. Acces au moindre privilege : limiter l'acces utilisateur avec JIT (Just-In-Time) et JEA (Just-Enough-Access).\n3. Supposer la compromission : minimiser le rayon d'explosion, segmenter les acces, chiffrer les communications.",
      },
      {
        title: 'Dans Entra ID',
        body: "Conditional Access met en oeuvre Zero Trust : MFA obligatoire, evaluation du risque de connexion, verification de la conformite de l'appareil, restrictions par localisation.",
      },
    ],
    diagram: {
      title: 'Modele Zero Trust',
      rows: [
        { nodes: [{ label: "Requete d'acces", color: '#8B5CF6', textColor: '#FFF' }] },
        { nodes: [{ label: '', color: 'transparent' }], arrow: true },
        { nodes: [
          { label: 'Identite verifiee ?', color: '#F59E0B', textColor: '#FFF' },
          { label: 'Appareil conforme ?', color: '#F59E0B', textColor: '#FFF' },
          { label: 'Localisation sure ?', color: '#F59E0B', textColor: '#FFF' },
        ]},
        { nodes: [{ label: '', color: 'transparent' }], arrow: true },
        { nodes: [
          { label: 'ACCES ACCORDE (moindre privilege)', color: '#10B981', textColor: '#FFF' },
          { label: 'ACCES REFUSE / MFA', color: '#EF4444', textColor: '#FFF' },
        ]},
      ],
    },
  },
  {
    id: 4,
    sequenceItemId: 'legacy-4',
    title: 'Isolation par Tenant',
    subtitle: 'Separation logique complete entre organisations',
    color: '#F59E0B',
    icon: 'cube',
    isRead: false,
    isBookmarked: true,
    sections: [
      {
        title: "Qu'est-ce qu'un tenant ?",
        body: "Un tenant Azure AD (Entra ID) est une instance dediee et isolee du service. Chaque organisation dispose de son propre tenant avec ses utilisateurs, groupes, applications et parametres de securite.",
      },
      {
        title: 'Isolation technique',
        body: "Les tenants sont completement etanches : les donnees, les politiques et les identites d'un tenant ne sont pas accessibles depuis un autre tenant. La communication inter-tenants necessite une configuration explicite (B2B, Azure Lighthouse).",
      },
      {
        title: "Cas d'usage EgoCrisis",
        body: "Dans EgoCrisis, le studio doit creer un tenant secondaire pour isoler les environnements de developpement et de production. Cela limite l'impact d'une compromission a un seul tenant.",
      },
    ],
    diagram: {
      title: 'Architecture multi-tenant',
      rows: [
        { nodes: [
          { label: 'Tenant Production\ncontoso.onmicrosoft.com', color: '#3B82F6', textColor: '#FFF' },
          { label: 'Tenant Dev\ncontoso-dev.onmicrosoft.com', color: '#10B981', textColor: '#FFF' },
        ]},
        { nodes: [{ label: 'Isolation complete — pas de flux par defaut', color: '#374151', textColor: '#9CA3AF' }] },
        { nodes: [
          { label: 'Users Prod', color: '#BFDBFE', textColor: '#1E3A8A' },
          { label: 'Apps Prod', color: '#BFDBFE', textColor: '#1E3A8A' },
          { label: 'Users Dev', color: '#A7F3D0', textColor: '#064E3B' },
          { label: 'Apps Dev', color: '#A7F3D0', textColor: '#064E3B' },
        ]},
        { nodes: [{ label: 'B2B Collaboration (si configure)', color: '#F59E0B', textColor: '#FFF' }] },
      ],
    },
  },
];

function scenarioCardsForMission(missionId?: number, chapterId?: number): ConceptCard[] | null {
  if (!missionId) return null;
  const scenario = getScenarioForMission(missionId);
  const chapter = scenario?.chapters?.[(chapterId ?? 1) - 1] ?? scenario?.chapters?.[0];
  if (!chapter) return null;
  const cardItems = chapter.sequence.filter(i => i.type === 'card');
  if (cardItems.length === 0) return null;
  const palette = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];
  const icons = ['document-text', 'library', 'book', 'layers', 'git-network'];
  return cardItems.map((item, idx) => {
    const body =
      typeof item.content === 'object' && 'body' in item.content
        ? String((item.content as any).body ?? '')
        : '';
    const title =
      typeof item.content === 'object' && 'title' in item.content
        ? String((item.content as any).title ?? item.title)
        : item.title;
    return {
      id: idx + 1,
      sequenceItemId: item.id,
      title,
      subtitle: item.title,
      color: palette[idx % palette.length],
      icon: icons[idx % icons.length],
      sections: [{ title: 'Fiche', body }],
      isRead: false,
      isBookmarked: false,
    } as ConceptCard;
  });
}

export default function MissionCardsScreen({ navigation: navProp, route: routeProp }: { navigation?: any; route?: any } = {}) {
  const navigation = navProp ?? useNavigation<any>();
  const route = routeProp ?? useRoute<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { missionId, chapterId, itemId, title } = route.params ?? {};
  const initialCards = useMemo(
    () => scenarioCardsForMission(missionId, chapterId) ?? EGOCRISIS_CARDS,
    [missionId, chapterId],
  );

  const resolveTargetCardIndex = (list: ConceptCard[]) => {
    if (list.length === 0) return 0;
    if (typeof itemId === 'number' && Number.isFinite(itemId) && missionId) {
      const scenario = getScenarioForMission(missionId);
      const chapter = scenario?.chapters?.[(chapterId ?? 1) - 1] ?? scenario?.chapters?.[0];
      if (chapter && itemId >= 1 && itemId <= chapter.sequence.length) {
        const targetSeqItem = chapter.sequence[itemId - 1];
        if (targetSeqItem?.type === 'card') {
          let cardPos = 0;
          for (let i = 0; i < itemId - 1; i += 1) {
            if (chapter.sequence[i]?.type === 'card') cardPos += 1;
          }
          if (cardPos >= 0 && cardPos < list.length) return cardPos;
        }
      }
    }
    if (typeof itemId === 'string' && itemId.length > 0) {
      const bySequenceId = list.findIndex(c => c.sequenceItemId === itemId);
      if (bySequenceId >= 0) return bySequenceId;
    }
    if (typeof title === 'string' && title.length > 0) {
      const byTitle = list.findIndex(c => c.subtitle === title || c.title === title);
      if (byTitle >= 0) return byTitle;
    }
    return 0;
  };

  const [cards, setCards] = useState(initialCards);
  const [currentIndex, setCurrentIndex] = useState(resolveTargetCardIndex(initialCards));
  const currentCard = cards[currentIndex];

  useEffect(() => {
    setCards(initialCards);
    setCurrentIndex(resolveTargetCardIndex(initialCards));
  }, [initialCards, itemId, title]);

  useEffect(() => {
    if (!currentCard) return;
    setCards(prev => prev.map(c => c.id === currentCard.id ? { ...c, isRead: true } : c));
  }, [currentCard?.id]);

  const toggleBookmark = (id: number) => {
    setCards(prev => prev.map(c => c.id === id ? { ...c, isBookmarked: !c.isBookmarked } : c));
  };

  const parseMarkupBlocks = (body: string): MarkupBlock[] => {
    const lines = body.replace(/\r\n/g, '\n').split('\n');
    const blocks: MarkupBlock[] = [];
    let i = 0;

    const isBlockBoundary = (line: string) =>
      /^\s*$/.test(line)
      || /^#{1,6}\s+/.test(line)
      || /^>\s?/.test(line)
      || /^\|\s?.+\|\s*$/.test(line)
      || /^-\s+/.test(line)
      || /^\d+\.\s+/.test(line);

    while (i < lines.length) {
      const raw = lines[i] ?? '';
      const line = raw.trim();

      if (!line) {
        i += 1;
        continue;
      }

      const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
      if (headingMatch) {
        blocks.push({ type: 'heading', level: headingMatch[1].length, text: headingMatch[2].trim() });
        i += 1;
        continue;
      }

      if (line.startsWith('>')) {
        const quoteLines: string[] = [];
        while (i < lines.length && /^\s*>\s?/.test(lines[i] ?? '')) {
          quoteLines.push((lines[i] ?? '').replace(/^\s*>\s?/, '').trim());
          i += 1;
        }
        blocks.push({ type: 'blockquote', text: quoteLines.join(' ') });
        continue;
      }

      if (/^\|\s?.+\|\s*$/.test(line)) {
        const tableRows: string[][] = [];
        while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i] ?? '')) {
          const row = (lines[i] ?? '')
            .trim()
            .slice(1, -1)
            .split('|')
            .map(cell => cell.trim());
          tableRows.push(row);
          i += 1;
        }
        const cleanedRows = tableRows.filter(r => !r.every(c => /^:?-{3,}:?$/.test(c)));
        if (cleanedRows.length > 0) {
          blocks.push({ type: 'table', rows: cleanedRows });
        }
        continue;
      }

      if (/^-\s+/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^-\s+/.test((lines[i] ?? '').trim())) {
          items.push((lines[i] ?? '').trim().replace(/^-\s+/, ''));
          i += 1;
        }
        blocks.push({ type: 'unordered-list', items });
        continue;
      }

      if (/^\d+\.\s+/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\d+\.\s+/.test((lines[i] ?? '').trim())) {
          items.push((lines[i] ?? '').trim().replace(/^\d+\.\s+/, ''));
          i += 1;
        }
        blocks.push({ type: 'ordered-list', items });
        continue;
      }

      const paragraphLines: string[] = [line];
      i += 1;
      while (i < lines.length && !isBlockBoundary((lines[i] ?? '').trim())) {
        paragraphLines.push((lines[i] ?? '').trim());
        i += 1;
      }
      blocks.push({ type: 'paragraph', text: paragraphLines.join(' ') });
    }

    return blocks;
  };

  const renderInlineMarkup = (text: string, textColor: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g).filter(Boolean);
    return parts.map((part, idx) => {
      if (/^\*\*[^*]+\*\*$/.test(part)) {
        return (
          <Text key={`b-${idx}`} style={[styles.inlineBold, { color: textColor }]}>
            {part.slice(2, -2)}
          </Text>
        );
      }
      if (/^\*[^*]+\*$/.test(part)) {
        return (
          <Text key={`i-${idx}`} style={[styles.inlineItalic, { color: textColor }]}>
            {part.slice(1, -1)}
          </Text>
        );
      }
      if (/^`[^`]+`$/.test(part)) {
        return (
          <Text key={`c-${idx}`} style={styles.inlineCode}>
            {part.slice(1, -1)}
          </Text>
        );
      }
      return (
        <Text key={`t-${idx}`} style={{ color: textColor }}>
          {part}
        </Text>
      );
    });
  };

  const renderMarkupBody = (body: string, cardColor: string) => {
    const blocks = parseMarkupBlocks(body);
    return blocks.map((block, idx) => {
      if (block.type === 'heading') {
        const headingStyle =
          block.level <= 2 ? styles.mdHeadingLg :
            block.level === 3 ? styles.mdHeadingMd : styles.mdHeadingSm;
        return (
          <Text key={`h-${idx}`} style={[headingStyle, { color: cardColor }]}>
            {renderInlineMarkup(block.text, cardColor)}
          </Text>
        );
      }

      if (block.type === 'paragraph') {
        return (
          <Text key={`p-${idx}`} style={[styles.sectionBody, { color: colors.textPrimary }]}>
            {renderInlineMarkup(block.text, colors.textPrimary)}
          </Text>
        );
      }

      if (block.type === 'blockquote') {
        return (
          <View key={`q-${idx}`} style={[styles.quoteBox, { borderLeftColor: cardColor, backgroundColor: cardColor + '10' }]}>
            <Text style={[styles.quoteText, { color: colors.textSecondary }]}>
              {renderInlineMarkup(block.text, colors.textSecondary)}
            </Text>
          </View>
        );
      }

      if (block.type === 'unordered-list') {
        return (
          <View key={`ul-${idx}`} style={styles.listWrap}>
            {block.items.map((item, itemIdx) => (
              <View key={`ul-item-${itemIdx}`} style={styles.listRow}>
                <Text style={[styles.listBullet, { color: cardColor }]}>•</Text>
                <Text style={[styles.listText, { color: colors.textPrimary }]}>
                  {renderInlineMarkup(item, colors.textPrimary)}
                </Text>
              </View>
            ))}
          </View>
        );
      }

      if (block.type === 'ordered-list') {
        return (
          <View key={`ol-${idx}`} style={styles.listWrap}>
            {block.items.map((item, itemIdx) => (
              <View key={`ol-item-${itemIdx}`} style={styles.listRow}>
                <Text style={[styles.listBulletNum, { color: cardColor }]}>{itemIdx + 1}.</Text>
                <Text style={[styles.listText, { color: colors.textPrimary }]}>
                  {renderInlineMarkup(item, colors.textPrimary)}
                </Text>
              </View>
            ))}
          </View>
        );
      }

      return (
        <ScrollView key={`t-${idx}`} horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tableWrap}>
          <View style={[styles.table, { borderColor: colors.border }]}>
            {block.rows.map((row, rowIdx) => (
              <View key={`row-${rowIdx}`} style={[styles.tableRow, rowIdx > 0 && { borderTopColor: colors.border, borderTopWidth: 1 }]}>
                {row.map((cell, cellIdx) => (
                  <View
                    key={`cell-${cellIdx}`}
                    style={[
                      styles.tableCell,
                      cellIdx > 0 && { borderLeftColor: colors.border, borderLeftWidth: 1 },
                      rowIdx === 0 && { backgroundColor: cardColor + '16' },
                    ]}
                  >
                    <Text style={[styles.tableCellText, { color: rowIdx === 0 ? cardColor : colors.textPrimary }]}>
                      {renderInlineMarkup(cell, rowIdx === 0 ? cardColor : colors.textPrimary)}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        </ScrollView>
      );
    });
  };

  const renderDiagramRow = (row: DiagramRow, rowIdx: number) => {
    if (row.arrow) {
      return (
        <View key={rowIdx} style={styles.diagramArrowRow}>
          <View style={[styles.diagramArrow, { backgroundColor: colors.border }]} />
          <View style={[styles.diagramArrowHead, { borderTopColor: colors.border }]} />
        </View>
      );
    }
    return (
      <View key={rowIdx} style={styles.diagramRow}>
        {row.nodes.map((node, nIdx) => {
          if (node.color === 'transparent') {
            return <View key={nIdx} style={{ height: 4 }} />;
          }
          return (
            <View
              key={nIdx}
              style={[styles.diagramNode, { backgroundColor: node.color, flex: 1, marginHorizontal: 3 }]}
            >
              <Text style={[styles.diagramNodeText, { color: node.textColor ?? '#FFF' }]} numberOfLines={3}>
                {node.label}
              </Text>
            </View>
          );
        })}
      </View>
    );
  };

  const renderCard = (card: ConceptCard) => (
    <View
      key={card.id}
      style={[styles.card, { backgroundColor: colors.cardBackground, borderLeftColor: card.color, shadowColor: colors.cardShadow ?? '#000' }]}
    >
      <View style={styles.cardHeader}>
        <View style={[styles.cardIconWrap, { backgroundColor: card.color + '22' }]}>
          <Ionicons name={card.icon as any} size={22} color={card.color} />
        </View>
        <View style={styles.cardHeaderText}>
          <View style={styles.cardTitleRow}>
            <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{card.title}</Text>
            {!card.isRead && <View style={[styles.unreadDot, { backgroundColor: card.color }]} />}
          </View>
          <Text style={[styles.cardSubtitle, { color: colors.textSecondary }]} numberOfLines={2}>
            {card.subtitle}
          </Text>
        </View>
        <View style={styles.cardHeaderActions}>
          <TouchableOpacity onPress={() => toggleBookmark(card.id)} style={styles.bookmarkBtn}>
            <Ionicons
              name={card.isBookmarked ? 'bookmark' : 'bookmark-outline'}
              size={18}
              color={card.isBookmarked ? card.color : colors.textTertiary}
            />
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.cardBody}>
        {card.sections.map((section, idx) => (
          <View key={idx} style={[styles.section, idx < card.sections.length - 1 && styles.sectionBorder, { borderBottomColor: colors.border }]}>
            <Text style={[styles.sectionTitle, { color: card.color }]}>{section.title}</Text>
            <View style={styles.markupContainer}>{renderMarkupBody(section.body, card.color)}</View>
          </View>
        ))}

        {card.diagram && (
          <View style={[styles.diagramContainer, { backgroundColor: isDarkTheme ? '#1F2937' : '#F9FAFB', borderColor: colors.border }]}>
            <View style={styles.diagramHeader}>
              <Ionicons name="git-network" size={14} color={card.color} />
              <Text style={[styles.diagramTitle, { color: colors.textSecondary }]}>{card.diagram.title}</Text>
            </View>
            <View style={styles.diagramBody}>
              {card.diagram.rows.map((row, idx) => renderDiagramRow(row, idx))}
            </View>
          </View>
        )}
      </View>
    </View>
  );

  const readCount = cards.filter(c => c.isRead).length;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Fiches Concepts</Text>
          <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
            {missionId === 201 || missionId === 102 ? 'Architecture Under Fire — AZ-305' : 'EgoCrisis — AZ-104'}
          </Text>
        </View>
        <View style={[styles.progressBadge, { backgroundColor: colors.surfaceSecondary }]}>
          <Text style={[styles.progressText, { color: colors.textSecondary }]}>{readCount}/{cards.length}</Text>
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentInner} showsVerticalScrollIndicator={false}>
        <View style={[styles.introCard, { backgroundColor: colors.primary + '11', borderColor: colors.primary + '33' }]}>
          <Ionicons name="information-circle" size={18} color={colors.primary} />
          <Text style={[styles.introText, { color: colors.textSecondary }]}>
            Une fiche par ecran. Utilisez Previous/Next pour passer a la suivante et garder une lecture sequentielle.
          </Text>
        </View>
        <View style={styles.paginationRow}>
          <TouchableOpacity
            style={[styles.navBtn, { borderColor: colors.border }, currentIndex === 0 && styles.navBtnDisabled]}
            onPress={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            activeOpacity={0.8}
          >
            <Ionicons name="chevron-back" size={16} color={currentIndex === 0 ? colors.textTertiary : colors.textPrimary} />
            <Text style={[styles.navBtnText, { color: currentIndex === 0 ? colors.textTertiary : colors.textPrimary }]}>Previous</Text>
          </TouchableOpacity>
          <Text style={[styles.pageIndicator, { color: colors.textSecondary }]}>
            {cards.length === 0 ? '0/0' : `${currentIndex + 1}/${cards.length}`}
          </Text>
          <TouchableOpacity
            style={[styles.navBtn, { borderColor: colors.border }, currentIndex >= cards.length - 1 && styles.navBtnDisabled]}
            onPress={() => setCurrentIndex(prev => Math.min(cards.length - 1, prev + 1))}
            disabled={currentIndex >= cards.length - 1}
            activeOpacity={0.8}
          >
            <Text style={[styles.navBtnText, { color: currentIndex >= cards.length - 1 ? colors.textTertiary : colors.textPrimary }]}>Next</Text>
            <Ionicons name="chevron-forward" size={16} color={currentIndex >= cards.length - 1 ? colors.textTertiary : colors.textPrimary} />
          </TouchableOpacity>
        </View>
        {currentCard ? renderCard(currentCard) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, gap: 12 },
  backButton: { padding: 8, marginLeft: -4 },
  headerCenter: { flex: 1 },
  headerTitle: { fontSize: 17, fontWeight: '700' },
  headerSubtitle: { fontSize: 12, marginTop: 1 },
  progressBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  progressText: { fontSize: 13, fontWeight: '600' },
  content: { flex: 1 },
  contentInner: { padding: 16 },
  introCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, padding: 12, borderRadius: 10, borderWidth: 1, marginBottom: 16 },
  introText: { flex: 1, fontSize: 13, lineHeight: 18 },
  paginationRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 8 },
  navBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8 },
  navBtnDisabled: { opacity: 0.45 },
  navBtnText: { fontSize: 13, fontWeight: '600' },
  pageIndicator: { fontSize: 12, fontWeight: '700' },
  card: {
    borderRadius: 12,
    marginBottom: 14,
    borderLeftWidth: 4,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 3,
    overflow: 'hidden',
  },
  cardHeader: { flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 },
  cardIconWrap: { width: 44, height: 44, borderRadius: 10, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardHeaderText: { flex: 1 },
  cardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 3 },
  cardTitle: { fontSize: 15, fontWeight: '700', flex: 1 },
  unreadDot: { width: 8, height: 8, borderRadius: 4 },
  cardSubtitle: { fontSize: 12, lineHeight: 17 },
  cardHeaderActions: { flexDirection: 'row', alignItems: 'center', gap: 10, flexShrink: 0 },
  bookmarkBtn: { padding: 4 },
  cardBody: { paddingHorizontal: 16, paddingBottom: 16 },
  markupContainer: { gap: 9 },
  section: { paddingVertical: 12 },
  sectionBorder: { borderBottomWidth: 1 },
  sectionTitle: { fontSize: 12, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 },
  sectionBody: { fontSize: 14, lineHeight: 21 },
  mdHeadingLg: { fontSize: 18, lineHeight: 24, fontWeight: '800', marginTop: 6, marginBottom: 2 },
  mdHeadingMd: { fontSize: 16, lineHeight: 22, fontWeight: '700', marginTop: 4, marginBottom: 2 },
  mdHeadingSm: { fontSize: 14, lineHeight: 20, fontWeight: '700', marginTop: 2, marginBottom: 1 },
  listWrap: { gap: 7 },
  listRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  listBullet: { width: 12, textAlign: 'center', fontSize: 14, lineHeight: 20, fontWeight: '700' },
  listBulletNum: { minWidth: 22, fontSize: 13, lineHeight: 20, fontWeight: '700' },
  listText: { flex: 1, fontSize: 14, lineHeight: 21 },
  quoteBox: { borderLeftWidth: 3, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 10 },
  quoteText: { fontSize: 13, lineHeight: 20, fontStyle: 'italic' },
  inlineBold: { fontWeight: '700' },
  inlineItalic: { fontStyle: 'italic' },
  inlineCode: {
    fontFamily: Platform.select({ web: 'ui-monospace, SFMono-Regular, Menlo, monospace', default: 'monospace' }),
    fontSize: 13,
    backgroundColor: 'rgba(120,120,120,0.18)',
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 4,
    color: '#E5E7EB',
  },
  tableWrap: { paddingVertical: 2 },
  table: { borderWidth: 1, borderRadius: 8, overflow: 'hidden', minWidth: 420 },
  tableRow: { flexDirection: 'row' },
  tableCell: { flex: 1, paddingHorizontal: 10, paddingVertical: 8, justifyContent: 'center' },
  tableCellText: { fontSize: 12, lineHeight: 18, fontWeight: '600' },
  diagramContainer: { marginTop: 12, borderRadius: 10, borderWidth: 1, padding: 14 },
  diagramHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12 },
  diagramTitle: { fontSize: 12, fontWeight: '600' },
  diagramBody: { gap: 2 },
  diagramRow: { flexDirection: 'row', alignItems: 'center', marginVertical: 2 },
  diagramNode: { borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8, alignItems: 'center', justifyContent: 'center' },
  diagramNodeText: { fontSize: 11, fontWeight: '600', textAlign: 'center', lineHeight: 15 },
  diagramArrowRow: { alignItems: 'center', height: 20, justifyContent: 'center' },
  diagramArrow: { width: 2, height: 12 },
  diagramArrowHead: { width: 0, height: 0, borderLeftWidth: 6, borderRightWidth: 6, borderTopWidth: 6, borderLeftColor: 'transparent', borderRightColor: 'transparent' },
});